import { createReducer } from 'typesafe-actions';

import { IState } from '@/store/types/search.types';
import { IProfileState } from '@/store/types/profile.types';
import { IProfile } from '@/store/models/profile.model';
import { IHandleAsynProfileActions } from '@/features/user/store/profile.types';

import { TUserAction, UserActionTypes } from './user.types';

const initialState: IProfileState = {
  profiles: {
    state: [],
  },
};

export const userReducer = createReducer<IProfileState, TUserAction>(initialState)
  /**
   * Create user reducers
   */
  .handleType(UserActionTypes.CREATE_USER, (state) => {
    return {
      ...state,
      create: { error: undefined, isLoading: true },
    };
  })

  .handleType(UserActionTypes.CREATE_USER_FAIL, (state, action) => {
    return {
      ...state,
      create: { error: action.error, isLoading: false },
    };
  })

  .handleType(UserActionTypes.CREATE_USER_SUCCESS, (state, action) => {
    return {
      ...state,
      create: { error: undefined, isLoading: false },
      profiles: {
        state: [{ state: { user: { state: action.user } } }, ...(state.profiles.state || [])],
      },
    };
  })
  /**
   * Update user reducers
   */
  .handleType(UserActionTypes.UPDATE_USER, (state, action) => handleUpdateUser(state, action.userId))

  .handleType(UserActionTypes.UPDATE_USER_FAIL, (state, action) =>
    handleUpdateUser(state, action.userId, { error: action.error }),
  )

  .handleType(UserActionTypes.UPDATE_USER_SUCCESS, (state, action) =>
    handleUpdateUser(state, action.user.userId, { state: action.user, error: undefined }),
  )

  /**
   * Update user reducers
   */
  .handleType(UserActionTypes.REFRESH_USER, (state, action) => handleUpdateUser(state, action.userId))

  .handleType(UserActionTypes.REFRESH_USER_FAIL, (state, action) =>
    handleUpdateUser(state, action.userId, { error: action.error }),
  )

  .handleType(UserActionTypes.REFRESH_USER_SUCCESS, (state, action) =>
    handleUpdateUser(state, action.user.userId, { state: action.user, error: undefined }),
  )

  /**
   * Deactivate user reducers
   */
  .handleType(UserActionTypes.DEACTIVATE_USER, (state, action) => handleDeactivateUser(state, action.userId))

  .handleType(UserActionTypes.DEACTIVATE_USER_FAIL, (state, action) =>
    handleDeactivateUser(state, action.userId, { error: action.error }),
  )

  .handleType(UserActionTypes.DEACTIVATE_USER_SUCCESS, (state, action) =>
    handleDeactivateUser(state, action.user.userId, { state: action.user, error: undefined }),
  )

  /**
   * Send Forgot Password reducers
   */
  .handleType(UserActionTypes.SEND_FORGOT_PASSWORD, (state, action) => handleForgotPassword(state, action.userId))

  .handleType(UserActionTypes.SEND_FORGOT_PASSWORD_FAIL, (state, action) =>
    handleForgotPassword(state, action.userId, { error: action.error }),
  )

  .handleType(UserActionTypes.SEND_FORGOT_PASSWORD_SUCCESS, (state, action) =>
    handleForgotPassword(state, action.userId, { error: undefined }),
  );

function handleDeactivateUser(
  currentState: IProfileState,
  userId: number,
  action?: IHandleAsynProfileActions,
): IProfileState {
  const profilesState = currentState.profiles.state?.map((profileState): IState<IProfile> => {
    if (profileState.state?.user.state?.userId === userId) {
      return {
        ...profileState,
        state: {
          ...profileState.state,
          user: {
            ...profileState.state.user,
            state: action?.state || profileState.state.user.state,
          },
          deactivate: {
            ...profileState.state?.deactivate,
            isLoading: !profileState.state?.deactivate?.isLoading,
            error: action?.error,
          },
        },
      };
    } else {
      return profileState;
    }
  });
  return {
    ...currentState,
    profiles: {
      ...currentState.profiles,
      state: profilesState,
    },
  };
}

function handleUpdateUser(
  currentState: IProfileState,
  userId: number,
  action?: IHandleAsynProfileActions,
): IProfileState {
  const profilesState = currentState.profiles.state?.map((profileState): IState<IProfile> => {
    if (profileState.state?.user.state?.userId === userId) {
      if (!profileState.state) return profileState;

      return {
        ...profileState,
        state: {
          ...profileState.state,
          user: {
            ...profileState.state?.user,
            isLoading: !profileState.state?.user.isLoading,
            ...action,
          },
        },
      };
    }
    return profileState;
  });

  return {
    ...currentState,
    profiles: {
      ...currentState.profiles,
      state: profilesState,
    },
  };
}

function handleForgotPassword(
  currentState: IProfileState,
  userId: number,
  action?: IHandleAsynProfileActions,
): IProfileState {
  const profilesState = currentState.profiles.state?.map((profileState): IState<IProfile> => {
    if (profileState.state?.user.state?.userId === userId) {
      if (!profileState.state) return profileState;

      return {
        ...profileState,
        state: {
          ...profileState.state,
          forgotPassword: {
            ...profileState.state?.forgotPassword,
            isLoading: !profileState.state?.forgotPassword?.isLoading,
            ...action,
          },
        },
      };
    }
    return profileState;
  });

  return {
    ...currentState,
    profiles: {
      ...currentState.profiles,
      state: profilesState,
    },
  };
}
